/* eslint-disable */
"use client";

import { X } from "lucide-react";
import { useEffect, useRef } from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, PointerEvent as ReactPointerEvent, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { cn } from "./utils";

/* ----------------------------------- card ----------------------------------- */
const MAX_TILT = 8; // degrees at the card corner
const MAX_LIFT = -9; // px raised on hover

type MotionState = {
  target: { rx: number; ry: number; lift: number; scale: number };
  current: { rx: number; ry: number; lift: number; scale: number };
  raf: number;
};

/**
 * 3D card. Rotation, lift and scale are eased toward their pointer-driven target
 * every animation frame (spring damping), which stays smooth even when the
 * pointer moves fast --- unlike CSS transitions retargeted per mousemove.
 */
export function Card({
  className,
  children,
  tilt = true,
}: {
  className?: string;
  children?: ReactNode;
  tilt?: boolean;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const motion = useRef<MotionState>({
    target: { rx: 0, ry: 0, lift: 0, scale: 1 },
    current: { rx: 0, ry: 0, lift: 0, scale: 1 },
    raf: 0,
  });

  const isReduced = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // spring toward the target ~12% per frame
  const tick = () => {
    const m = motion.current;
    const c = m.current;
    const t = m.target;
    const K = 0.12;
    c.rx += (t.rx - c.rx) * K;
    c.ry += (t.ry - c.ry) * K;
    c.lift += (t.lift - c.lift) * K;
    c.scale += (t.scale - c.scale) * K;

    const el = cardRef.current;
    if (el) {
      el.style.transform = `rotateX(${c.rx.toFixed(3)}deg) rotateY(${c.ry.toFixed(
        3
      )}deg) translate3d(0, ${c.lift.toFixed(2)}px, 0) scale(${c.scale.toFixed(4)})`;
    }

    const settled =
      Math.abs(t.rx - c.rx) < 0.02 &&
      Math.abs(t.ry - c.ry) < 0.02 &&
      Math.abs(t.lift - c.lift) < 0.05 &&
      Math.abs(t.scale - c.scale) < 0.0004;

    if (settled) {
      const snap = cardRef.current;
      if (snap) {
        if (t.lift === 0 && t.rx === 0 && t.ry === 0 && t.scale === 1) {
          snap.style.transform = ""; // fully parked --- clear the inline transform
        } else {
          snap.style.transform = `rotateX(${t.rx.toFixed(3)}deg) rotateY(${t.ry.toFixed(
            3
          )}deg) translate3d(0, ${t.lift.toFixed(2)}px, 0) scale(${t.scale.toFixed(4)})`;
        }
      }
      m.raf = 0;
      return;
    }
    m.raf = requestAnimationFrame(tick);
  };

  const kick = () => {
    if (isReduced()) return;
    const m = motion.current;
    if (!m.raf) m.raf = requestAnimationFrame(tick);
  };

  useEffect(() => {
    return () => {
      const m = motion.current;
      if (m.raf) cancelAnimationFrame(m.raf);
    };
  }, []);

  const handleMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!tilt || e.pointerType === "touch" || isReduced()) return;
    const wrap = wrapRef.current;
    const card = cardRef.current;
    if (!wrap || !card) return;
    // measure the untransformed wrapper so tilting doesn't distort our own math
    const rect = wrap.getBoundingClientRect();
    const px = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const py = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    // damp the tilt on very wide cards so big tables stay readable
    const damp = Math.min(1, 560 / Math.max(rect.width, 1));
    motion.current.target = {
      rx: (0.5 - py) * 2 * MAX_TILT * damp,
      ry: (px - 0.5) * 2 * MAX_TILT * damp,
      lift: MAX_LIFT,
      scale: 1.015,
    };
    card.style.setProperty("--mx", `${(px * 100).toFixed(1)}%`);
    card.style.setProperty("--my", `${(py * 100).toFixed(1)}%`);
    kick();
  };

  const handleEnter = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!tilt || e.pointerType === "touch" || isReduced()) return;
    motion.current.target = { ...motion.current.target, lift: MAX_LIFT, scale: 1.015 };
    kick();
  };

  const handleLeave = () => {
    motion.current.target = { rx: 0, ry: 0, lift: 0, scale: 1 };
    kick();
  };

  // grid/flow placement must stay on the outer wrapper, styling on the card itself
  const { layout, styling } = splitLayoutClasses(className);

  return (
    <div
      ref={wrapRef}
      className={cn("card3d-wrap", layout)}
      onPointerMove={handleMove}
      onPointerEnter={handleEnter}
      onPointerLeave={handleLeave}
    >
      <div
        ref={cardRef}
        className={cn("card3d h-full rounded-2xl", styling)}
      >
        <span className="card3d-glare" aria-hidden />
        <div className="card3d-content">{children}</div>
      </div>
    </div>
  );
}

const LAYOUT_RE = /^(?:[a-z0-9]+:)*(?:col-span-|row-span-|col-start-|row-start-|order-)/;

function splitLayoutClasses(className?: string) {
  if (!className) return { layout: "", styling: "" };
  const layout: string[] = [];
  const styling: string[] = [];
  for (const c of className.split(/\s+/).filter(Boolean)) {
    (LAYOUT_RE.test(c) ? layout : styling).push(c);
  }
  return { layout: layout.join(" "), styling: styling.join(" ") };
}

/* ---------------------------------- button ----------------------------------- */
type ButtonVariant = "primary" | "accent" | "outline" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg";

const buttonVariants: Record<ButtonVariant, string> = {
  primary:
    "bg-primary text-on-primary hover:bg-primary-strong border border-transparent shadow-[0_10px_26px_-12px_var(--primary)]",
  accent:
    "bg-accent text-on-accent hover:bg-accent-strong border border-transparent shadow-[0_10px_26px_-12px_var(--accent)]",
  outline: "border border-border bg-surface2 text-text hover:border-border-strong hover:bg-surface3",
  ghost: "text-muted hover:text-text hover:bg-surface2 border border-transparent",
  danger:
    "border border-transparent bg-[color-mix(in_oklab,var(--danger)_14%,transparent)] text-danger hover:bg-[color-mix(in_oklab,var(--danger)_22%,transparent)]",
};

const buttonSizes: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-xs gap-1.5",
  md: "h-10 px-4 text-sm gap-2",
  lg: "h-12 px-6 text-sm gap-2",
};

export function Button({
  variant = "primary",
  size = "md",
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize }) {
  return (
    <button
      className={cn(
        "inline-flex cursor-pointer items-center justify-center rounded-xl font-semibold transition-all duration-200 active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50",
        buttonVariants[variant],
        buttonSizes[size],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/* ----------------------------------- field ----------------------------------- */
export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("block", className)}>
      <span className="mb-1.5 flex items-baseline justify-between text-xs font-semibold tracking-wide text-muted">
        <span>{label}</span>
        {hint && <span className="text-[10px] font-medium text-faint">{hint}</span>}
      </span>
      {children}
    </label>
  );
}

const controlBase =
  "w-full rounded-xl border border-border bg-surface2 px-3.5 text-sm text-text placeholder:text-faint outline-none transition-all focus:border-primary focus:ring-4 focus:ring-[color-mix(in_oklab,var(--primary)_18%,transparent)]";

export function TextInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(controlBase, "h-10", className)} {...props} />;
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cn(controlBase, "h-10 cursor-pointer appearance-none pr-9", className)} {...props}>
      {children}
    </select>
  );
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(controlBase, "py-2.5 leading-relaxed", className)} {...props} />;
}

export function Switch({
  checked,
  onChange,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  className?: string;
}) {
  const track = cn(
    "relative inline-flex h-7 w-12 cursor-pointer shrink-0 items-center rounded-full border border-border/70 transition-all duration-200",
    disabled ? "opacity-60 cursor-not-allowed pointer-events-none" : "",
    checked
      ? "bg-gradient-to-br from-primary to-[color-mix(in_oklab,var(--primary)_60%,var(--accent)_40%)] border-transparent shadow-[inset_0_1px_0_0_rgba(255,255,255,0.2),0_10px_24px_-14px_var(--primary)]"
      : "bg-surface2"
  );
  const knob = cn(
    "absolute left-0.5 grid h-5 w-5 place-items-center rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.25),0_1px_0_rgba(255,255,255,0.25)_inset] transition-all duration-200",
    checked ? "translate-x-5" : "translate-x-0"
  );
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={cn(track, className)}
    >
      <span className={knob} />
    </button>
  );
}

/* ----------------------------------- modal ----------------------------------- */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  wide = false,
  fullscreen = false,
  hideBackdrop = false,
  panelClassName = "",
  contentClassName = "",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  children?: ReactNode;
  wide?: boolean;
  fullscreen?: boolean;
  hideBackdrop?: boolean;
  panelClassName?: string;
  contentClassName?: string;
}) {
  if (!open) return null;
  return (
    <div
      className={cn(
        "absolute inset-0 z-[130]",
        fullscreen ? "flex h-full w-full items-stretch justify-stretch p-0" : "flex items-center justify-center p-4 sm:p-6"
      )}
    >
      {!hideBackdrop && (
        <div
          className="fade-in absolute inset-0 bg-[color-mix(in_oklab,var(--bg)_72%,black)] backdrop-blur-sm"
          onClick={onClose}
        />
      )}
      <div
        className={cn(
          "modal-in relative flex max-h-full w-full flex-col overflow-hidden rounded-3xl border border-border bg-surface shadow-[var(--shadow)]",
          fullscreen
            ? "h-full max-h-full max-w-none rounded-none border-0 shadow-none"
            : wide
              ? "sm:max-w-4xl"
              : "sm:max-w-lg",
          panelClassName
        )}
      >
        <div className="flex flex-shrink-0 items-start gap-3.5 border-b border-border px-6 py-5">
          {icon && (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[color-mix(in_oklab,var(--accent)_15%,transparent)] text-accent">
              {icon}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-lg font-bold text-text">{title}</h2>
            {subtitle && <p className="mt-0.5 text-xs leading-relaxed text-muted">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-faint transition-colors hover:bg-surface2 hover:text-text"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className={cn("flex-1 overflow-y-auto px-6 py-5", fullscreen ? "min-h-0" : "", contentClassName)}>{children}</div>
      </div>
    </div>
  );
}

/* ----------------------------------- pill ------------------------------------ */
type PillTone = "blue" | "yellow" | "green" | "red" | "gray" | "sky" | "muted";
const pillTones: Record<PillTone, string> = {
  blue: "bg-[color-mix(in_oklab,var(--primary)_14%,transparent)] text-primary-strong",
  yellow: "bg-[color-mix(in_oklab,var(--accent)_16%,transparent)] text-accent-strong",
  green: "bg-[color-mix(in_oklab,var(--success)_14%,transparent)] text-success",
  red: "bg-[color-mix(in_oklab,var(--danger)_12%,transparent)] text-danger",
  gray: "bg-surface3 text-muted",
  sky: "bg-[color-mix(in_oklab,var(--info)_14%,transparent)] text-info",
  muted: "bg-surface2/60 text-faint",
};

export function Pill({
  tone = "gray",
  className,
  children,
}: {
  tone?: PillTone;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap",
        pillTones[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

/* -------------------------------- empty state --------------------------------- */
export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-surface2 text-faint">
        {icon}
      </span>
      <p className="font-display text-base font-bold text-text">{title}</p>
      <p className="mt-1 max-w-xs text-sm leading-relaxed text-muted">{description}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/* ---------------------------------- type badge --------------------------------- */
export function EmployeeTypeBadge({ type }: { type: string }) {
  return type === "313" ? (
    <Pill tone="blue">
      <span className="h-1.5 w-1.5 rounded-full bg-primary-strong" />
      Type 313
    </Pill>
  ) : (
    <Pill tone="yellow">
      <span className="h-1.5 w-1.5 rounded-full bg-accent-strong" />
      Type 261
    </Pill>
  );
}

export function StatusPill({ status }: { status: string }) {
  if (status === "active" || status === "finalized")
    return (
      <Pill tone="green">
        <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-success" />
        {status === "active" ? "Active" : "Finalized"}
      </Pill>
    );
  if (status === "paid") return <Pill tone="blue">Fully Paid</Pill>;
  if (status === "voided") return <Pill tone="red">Voided</Pill>;
  return <Pill tone="gray">Inactive</Pill>;
}
